import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { SeoBlock } from "../components/SeoBlock";
import { useContent } from "../lib/content";
import { heroCardStyle } from "../lib/heroCard";
import { Icon } from "../lib/icons";
import { resolveImage } from "../lib/realisations";

export default function Metiers() {
  const { hash } = useLocation();
  const c = useContent("metiers");
  const METIERS = c.metiers || [];

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [hash, METIERS]);

  return (
    <div data-testid="page-metiers">
      <SEO path="/metiers" title="Nos Métiers — Économie, MOEx & OPC | ECO-GEST PATRIMOINE" description="Trois métiers complémentaires : économie de la construction, maîtrise d'œuvre d'exécution et OPC (ordonnancement, pilotage, coordination). Découvrez nos expertises." />

      <section className="relative h-[50vh] min-h-[360px] bg-[#1A1A1A] text-white overflow-hidden flex items-end">
        <img src={resolveImage(c.hero_image)} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: ((c.hero_image_opacity ?? 100) / 100) }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pb-14 w-full">
          <div
            className="inline-block border-l-2 border-[#E60000] rounded-r-lg px-6 py-5 shadow-xl shadow-black/15"
            style={heroCardStyle(c)}
          >
            <p className="inline-flex items-center gap-3 text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-white">
              <span className="inline-block w-8 h-px bg-[#E60000]" />
              {c.hero_breadcrumb}
            </p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] text-white mt-3">{c.hero_title}</h1>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 text-center">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight leading-tight">
              {c.intro_title_line1}<br />{c.intro_title_line2}
            </h2>
            <div className="w-16 h-0.5 bg-[#E60000] mx-auto mt-6" />
          </Reveal>
        </div>
      </section>

      <div className="pb-24">
        {METIERS.map((m) => (
          <section key={m.id} id={m.id} data-testid={`metier-section-${m.id}`} className="py-14 border-t border-gray-200 scroll-mt-32">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <Reveal className="lg:col-span-1 flex justify-center lg:justify-start">
                <div className="w-16 h-16 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center shrink-0">
                  <Icon name={m.icon} className="w-7 h-7 text-[#1A1A1A]" strokeWidth={1.5} />
                </div>
              </Reveal>
              <Reveal delay={100} className="lg:col-span-6">
                <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight mb-4">{m.title}</h3>
                <p className="text-gray-700 leading-relaxed mb-6">{m.text}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {(m.missions || []).map((mi, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <span className="mt-2 inline-block w-1.5 h-1.5 bg-[#E60000] shrink-0" />
                      <span>{mi}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={250} className="lg:col-span-5">
                <div className="aspect-[5/4] overflow-hidden bg-gray-100 group">
                  <img loading="lazy" src={resolveImage(m.image)} alt={m.title} className="img-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                </div>
              </Reveal>
            </div>
          </section>
        ))}
      </div>

      <SeoBlock title={c.seo_title} sections={c.seo_sections || []} />

      <section className="bg-[#1A1A1A] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="font-display text-xl md:text-2xl font-semibold tracking-tight">{c.cta_title}</h3>
            <p className="text-sm text-gray-400 mt-2">{c.cta_text}</p>
          </div>
          <Link to="/contact" data-testid="metiers-cta-contact" className="inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#B30000] text-white px-7 py-4 text-sm font-medium tracking-wide uppercase transition-colors w-fit">
            {c.cta_button} <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>
      </section>
    </div>
  );
}
